import 'reflect-metadata';
import * as ENGINE from '@gnsx/genesys.js';

import { collectActorParentEdgesFromTree, type SceneTreeActorParentEdge } from './sceneTreeActorEdges';

import type { ViewMode } from '@/editor/store';
import type { CameraName } from '@/editor/store/models/';

/**
 * EditorData for Actors/Components (editorData)
 */
export interface EditorObjectData {
  displayName?: string; // DEPRECATED: Use name instead
  hidden?: boolean; // Whether the actor/component is hidden in the editor scene
  selectable?: boolean; // Whether the actor/component is selectable in the editor scene
  prefabAsset?: string; // (Runtime only) The name of the prefab asset that this actor is based on
}

export const TRANSIENT_PREFAB_ASSET_EDITOR_DATA_KEYS = [
  'prefabAsset',
  'hidden',
  'selectable',
] as const;

// register a custom serializer for editorData to only serialize non-transient prefab asset related data
const originalMetaData = ENGINE.getPropertyMetadata(ENGINE.Actor.prototype)['editorData'];
ENGINE.registerPropertyMetadata(ENGINE.Actor, {
  editorData: {
    ...originalMetaData,
    serialize: (value: any, dumper: ENGINE.IDumper) => {
      for (const [k, v] of Object.entries(value)) {
        if (dumper.hasFlags(ENGINE.DumperFlags.AsPrefab) && TRANSIENT_PREFAB_ASSET_EDITOR_DATA_KEYS.includes(k as any)) {
          continue;
        }
        dumper.dumpValue(k, v);
      }
    }
  }
});

/**
 * Serializable tree node data for persistence
 */
export interface SceneTreeData {
  id: string;
  name?: string; // Display name for the node (saved for folders only)
  nodeType: 'scene' | 'folder' | 'actor';
  isExpanded?: boolean; // Expanded state
  children?: SceneTreeData[];
}

export function traverseSceneTreeData(node: SceneTreeData, visit: (node: SceneTreeData) => void, visited: Set<string> = new Set()) {
  if (visited.has(node.id)) return;
  visited.add(node.id);
  visit(node);
  node.children?.forEach(child => traverseSceneTreeData(child, visit, visited));
}

/**
 * Walks saved outliner data and returns actor-under-actor edges using the same parent-stack
 * rules as `actorParenting.rebuildParentingFromTree` (scene and folders do not become actor
 * parents; nested actors do).
 */
export function getActorParentChildEdgesFromSceneTreeData(root: SceneTreeData): SceneTreeActorParentEdge[] {
  const result = collectActorParentEdgesFromTree(root);
  result.skippedNodeIds.forEach(id => {
    console.warn(`[editorClassData] Skipping repeated scene tree node ${id}: would create cycle`);
  });
  return result.edges;
}

/**
 * EditorData for World
 */
export interface EditorWorldData {
  camera?: Record<CameraName, EditorCameraData>;
  sceneTree?: SceneTreeData; // Serializable tree structure for persistence
}

/** Camera data stored in editor world */
export interface CameraViewState {
  position: [number, number, number];
  target: [number, number, number];
  zoom: number;
}

export interface EditorCameraData {
  version?: number;
  viewMode?: ViewMode;
  flySpeed?: number;
  views?: Partial<Record<ViewMode, CameraViewState>>;
}
